import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useConfirmPasswordReset } from "@/api/auth";
import { AgencyLogo } from "@/components/agency-logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { AxiosError } from "axios";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid") || "";
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const resetConfirm = useConfirmPasswordReset();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    resetConfirm.mutate(
      { uid, token, new_password: password },
      {
        onSuccess: () => setSuccess(true),
        onError: (err) => {
          const axiosErr = err as AxiosError<{ error?: { message?: string } }>;
          setError(axiosErr.response?.data?.error?.message || "Invalid or expired reset link.");
        },
      }
    );
  };

  if (!uid || !token) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-background overflow-hidden">
        <div className="login-grid" />
        <div className="login-glow" />
        <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border/60 bg-surface/90 p-8 shadow-elevated backdrop-blur-sm text-center">
          <p className="text-sm text-danger">Invalid reset link. Missing required parameters.</p>
          <Link to="/login" className="mt-4 inline-block text-sm font-medium text-accent hover:underline">
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background overflow-hidden">
      <div className="login-grid" />
      <div className="login-glow" />

      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border/60 bg-surface/90 p-8 shadow-elevated backdrop-blur-sm">
        <div className="mb-10 flex flex-col items-center gap-4">
          <AgencyLogo className="h-8 text-text-primary" />
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-text-muted">
            Set New Password
          </p>
        </div>

        {success ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-text-secondary">
              Your password has been reset successfully. You can now sign in with your new password.
            </p>
            <Link to="/login" className="text-sm font-medium text-accent hover:underline">
              Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">
                New Password
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength={8}
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">
                Confirm Password
              </label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                minLength={8}
              />
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
            <Button type="submit" className="w-full" disabled={resetConfirm.isPending}>
              {resetConfirm.isPending ? "Resetting..." : "Reset Password"}
            </Button>
            <div className="text-center">
              <Link to="/login" className="text-sm text-text-muted hover:text-text-primary">
                Back to Sign In
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
